import { useMemo } from 'react'

import {
  useFilters,
  useModals,
  useSetState,
  useShowModal,
} from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import handleCallbackAction from '@functions/handleCallbackAction'

import AddAccountingItemModal from '@modals/AddAccountingItemModal'
import CreateAgencyPayableInvoiceModal from '@modals/CreateAgencyPayableInvoiceModal'
import CreateFinanceApplicationModal from '@modals/CreateFinanceApplicationModal'
import CreateOrEditCampaignContributionModal from '@modals/CreateOrEditCampaignContributionModal'
import CreateOrEditCreditNoteModal from '@modals/CreateOrEditCreditNoteModal'
import CreateOrEditInvoiceModal from '@modals/CreateOrEditInvoiceModal'

import type {
  CampaignContributionModel, CampaignModel, CreditNoteModel, FinanceApplicationModel, InvoiceModel,
} from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showAddAccountingItemModal: false,
  showCreateAgencyPayableInvoiceModal: false,
  showCreateFinanceApplicationModal: false,
  showCreateOrEditCreditNoteModal: false,
  showCreateOrEditCampaignContributionModal: false,
  showCreateOrEditInvoiceModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    AddAccountingItemModal: {
      closeModal: () => setState({ showAddAccountingItemModal: false }),
    },
    CreateAgencyPayableInvoiceModal: {
      // layered modals that both need to close upon invoice creation
      closeAllModals: () => setState({
        showAddAccountingItemModal: false,
        showCreateAgencyPayableInvoiceModal: false,
      }),
      closeModal: () => setState({ showCreateAgencyPayableInvoiceModal: false }),
      createInvoice: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateFinanceApplicationModal: {
      closeModal: () => setState({ showCreateFinanceApplicationModal: false }),
      createFinanceApplication: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditCreditNoteModal: {
      closeAllModals: () => setState({
        showAddAccountingItemModal: false,
        showCreateOrEditCreditNoteModal: false,
      }),
      closeModal: () => setState({ showCreateOrEditCreditNoteModal: false }),
      createCreditNote: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      createCreditNoteItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateCreditNote: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditCampaignContributionModal: {
      // layered modals that both need to close upon contribution creation
      closeAllModals: () => setState({
        showAddAccountingItemModal: false,
        showCreateOrEditCampaignContributionModal: false,
      }),
      closeModal: () => setState({ showCreateOrEditCampaignContributionModal: false }),
      createCampaignContribution: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      editCampaignContribution: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditInvoiceModal: {
      closeModal: () => setState({ showCreateOrEditInvoiceModal: false }),
      createInvoice: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateInvoice: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const Accounting = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showAddAccountingItemModal,
    showCreateAgencyPayableInvoiceModal,
    showCreateFinanceApplicationModal,
    showCreateOrEditCreditNoteModal,
    showCreateOrEditCampaignContributionModal,
    showCreateOrEditInvoiceModal,
  } = state

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    defaultFiltersModifiedAt,
    pageFilters,
  } = useFilters({ defaultFilters: { itemType: 'campaign_balance' } })

  const modalContext = useModals({ setState })
  const { callbacks: { setModalData }, baseModalKey, topModalKey } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showAddAccountingItemModal: (payload: { campaign?: CampaignModel }) => {
        setModalData('AddAccountingItemModal', payload)
        setState({ showAddAccountingItemModal: true })
      },
      showCreateAgencyPayableInvoiceModal: (payload: { invoice?: InvoiceModel }) => {
        setModalData('CreateAgencyPayableInvoiceModal', payload)
        setState({ showCreateAgencyPayableInvoiceModal: true })
      },
      showCreateFinanceApplicationModal: (payload: { financeApplication: FinanceApplicationModel }) => {
        setModalData('CreateFinanceApplicationModal', payload)
        setState({ showCreateFinanceApplicationModal: true })
      },
      showCreateOrEditCreditNoteModal: (payload: { creditNote?: CreditNoteModel }) => {
        setModalData('CreateOrEditCreditNoteModal', payload)
        setState({ showCreateOrEditCreditNoteModal: true })
      },
      showCreateOrEditCampaignContributionModal: (payload: { campaignContribution?: CampaignContributionModel }) => {
        setModalData('CreateOrEditCampaignContributionModal', payload)
        setState({ showCreateOrEditCampaignContributionModal: true })
      },
      showCreateOrEditInvoiceModal: (payload: { invoice?: InvoiceModel }) => {
        setModalData('CreateOrEditInvoiceModal', payload)
        setState({ showCreateOrEditInvoiceModal: true })
      },
    },
    defaultFilters,
  }), [defaultFiltersModifiedAt, baseModalKey, topModalKey]) // modalContext dependency required if layering modals

  useShowModal({
    modalKey: 'AddAccountingItemModal',
    options: {
      // @ts-expect-error TS2322
      callbacks: pageContext.callbacks,
    },
  })

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <AddAccountingItemModal
          callbacks={callbacks('AddAccountingItemModal', setState)}
          showModal={showAddAccountingItemModal}
        />

        <CreateAgencyPayableInvoiceModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateAgencyPayableInvoiceModal', setState)}
          showModal={showCreateAgencyPayableInvoiceModal}
        />

        <CreateFinanceApplicationModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateFinanceApplicationModal', setState)}
          showModal={showCreateFinanceApplicationModal}
        />

        <CreateOrEditCreditNoteModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditCreditNoteModal', setState)}
          showModal={showCreateOrEditCreditNoteModal}
        />

        <CreateOrEditCampaignContributionModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditCampaignContributionModal', setState)}
          showModal={showCreateOrEditCampaignContributionModal}
        />

        <CreateOrEditInvoiceModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditInvoiceModal', setState)}
          showModal={showCreateOrEditInvoiceModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Accounting
